import { PageAccessMap, SubMenu } from 'src/Menus';

/**
 * Scores how *specifically* a route's path definition matches the current
 * pathname. Returns the length of the matched base, or -1 for no match.
 *
 * Route paths in PageAccessMap come in three flavors:
 *   - exact:  '/compartments'
 *   - prefix: '/users/*'  (react-router catch-all)
 *   - nested: '/picking/zones'  vs.  '/pickingsides'  (no shared prefix)
 *
 * Treats `/foo/*` as "any path under /foo" and bare `/foo` as "any path
 * under /foo too" — both make the parent submenu read as active when the
 * user is on a child page (e.g. `/areas/42/users` still lights up Areas).
 *
 * The `/` boundary check prevents `/picking` from spuriously matching
 * `/pickingsides` (and `/system` from matching `/system-config`).
 *
 * Returning the base length lets callers prefer the most specific match
 * instead of whichever entry happens to come first: given both `/picking`
 * and `/picking/zones`, the pathname `/picking/zones` scores higher
 * against the deeper route, so that one wins.
 */
export const matchScore = (routePath: string | undefined, pathname: string): number => {
  if (!routePath) {
    return -1;
  }
  const base = routePath.replace(/\/\*$/, '');
  if (pathname === base || pathname.startsWith(base + '/')) {
    return base.length;
  }
  return -1;
};

/**
 * Whether a route's path definition matches the current pathname at all.
 * Thin boolean wrapper over {@link matchScore} for call sites that don't
 * need to compare specificity.
 */
export const matchesPath = (routePath: string | undefined, pathname: string): boolean =>
  matchScore(routePath, pathname) >= 0;

/**
 * Returns the SubMenu that contains the route currently in view, or
 * undefined if the pathname doesn't correspond to any menu entry (e.g.
 * /me/settings, /login, etc).
 *
 * When several routes match (a parent and its child both match a deep
 * pathname), the most specific one wins so navigation expands the section
 * that actually owns the current page.
 */
export const findActiveSubMenu = (pathname: string): SubMenu | undefined => {
  let bestSubMenu: SubMenu | undefined;
  let bestScore = -1;
  for (const key of Object.keys(PageAccessMap)) {
    const access = PageAccessMap[key];
    // URL-only pages have no section to activate — skip them so e.g. /users
    // (hidden from the sidebar) leaves every section collapsed.
    if (access.menu === undefined) {
      continue;
    }
    const score = matchScore(access.route.path, pathname);
    if (score > bestScore) {
      bestScore = score;
      bestSubMenu = access.menu.subMenu;
    }
  }
  return bestSubMenu;
};
